import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import toast from 'react-hot-toast';

// Stack of active modal close handlers (LIFO - Last In, First Out)
const modalStack = [];

// Custom back action handlers sorted by priority descending
const actionHandlers = [];

// Timestamp of last back button press on root screen (for double-tap detection)
let lastBackPressTime = 0;

/**
 * Register an active modal's close callback.
 * When hardware back is pressed, the topmost modal is closed first.
 * Returns an unregister cleanup function.
 */
export function registerModal(closeFn) {
  if (typeof closeFn !== 'function') return () => {};
  const item = { closeFn, id: Symbol('modal') };
  modalStack.push(item);

  return () => {
    const idx = modalStack.findIndex(m => m.id === item.id);
    if (idx !== -1) {
      modalStack.splice(idx, 1);
    }
  };
}

/**
 * Check if any modal is currently open.
 */
export function hasOpenModals() {
  return modalStack.length > 0;
}

/**
 * Closes the topmost active modal if one exists.
 * Returns true if a modal was closed, false otherwise.
 */
export function closeTopModal() {
  if (modalStack.length > 0) {
    const top = modalStack.pop();
    try {
      if (typeof top?.closeFn === 'function') {
        top.closeFn();
      }
    } catch (err) {
      console.error('[BackButtonManager] Error closing modal on back:', err);
    }
    return true;
  }
  return false;
}

/**
 * Register a custom back action handler (e.g. mobile drawer menu, search overlay).
 * Higher priority runs first.
 * Handler should return true if it consumed the back event, false otherwise.
 */
export function registerBackAction(handler, priority = 0) {
  if (typeof handler !== 'function') return () => {};
  const item = { handler, priority, id: Symbol('backAction') };
  actionHandlers.push(item);
  actionHandlers.sort((a, b) => b.priority - a.priority);

  return () => {
    const idx = actionHandlers.findIndex(a => a.id === item.id);
    if (idx !== -1) {
      actionHandlers.splice(idx, 1);
    }
  };
}

/**
 * Handles the standard Android double-tap to exit pattern on root screens.
 */
export function handleRootExit(exitMessage = "Press back again to exit") {
  const now = Date.now();
  if (now - lastBackPressTime < 2000) {
    lastBackPressTime = 0;
    if (Capacitor.isNativePlatform() || Capacitor.isPluginAvailable('App')) {
      CapApp.exitApp().catch(() => {});
    }
  } else {
    lastBackPressTime = now;
    toast(exitMessage, {
      id: 'app-exit-toast',
      duration: 2000,
      icon: '👋',
      style: {
        borderRadius: '12px',
        background: '#1e293b',
        color: '#f8fafc',
        fontSize: '13px',
        fontWeight: '500',
      },
    });
  }
}

let isInitialized = false;
let globalNavigateBack = null;

/**
 * Initialize back button listener for Capacitor native Android / iOS
 * and desktop/web fallbacks.
 */
export function initBackButtonManager(onNavigateBack) {
  globalNavigateBack = onNavigateBack;

  if (isInitialized) return;
  isInitialized = true;

  const isNative = Capacitor.isNativePlatform();
  const hasAppPlugin = Capacitor.isPluginAvailable('App');

  if (isNative || hasAppPlugin) {
    try {
      CapApp.addListener('backButton', async (event) => {
        // Priority 1: Topmost active modal
        if (closeTopModal()) {
          return;
        }

        // Priority 2: Registered custom back actions (e.g. mobile drawer)
        for (const item of [...actionHandlers]) {
          try {
            const handled = item.handler();
            if (handled) return;
          } catch (err) {
            console.error('[BackButtonManager] Error in back action handler:', err);
          }
        }

        // Priority 3: In-App screen navigation
        if (typeof globalNavigateBack === 'function') {
          try {
            const handled = globalNavigateBack();
            if (handled) return;
          } catch (err) {
            console.error('[BackButtonManager] Error in navigation back handler:', err);
          }
        }

        // Priority 4: Root screen fallback (double tap to exit)
        handleRootExit();
      }).catch((err) => {
        console.warn('[BackButtonManager] Capacitor backButton listener error:', err);
      });
    } catch (err) {
      console.warn('[BackButtonManager] Could not initialize Capacitor backButton listener:', err);
    }
  }

  // Handle Escape key on desktop/browser to close topmost modal
  if (typeof window !== 'undefined') {
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && hasOpenModals()) {
        closeTopModal();
      }
    });
  }
}
